import { z } from "zod"

import { MAX_RESOLVED_REF_LENGTH } from "@productlane/lib"

// What a custom data read is about: a thread, or a contact / company record.
export const CustomDataTarget = z.object({
	type: z.enum(["thread", "contact", "company"]),
	id: z.string().min(1),
})
export type CustomDataTarget = z.infer<typeof CustomDataTarget>

// Names a component's value so a connector tool policy can inject it as
// "http.<ref>". Never rendered, never shown to the model.
export const CUSTOM_DATA_REF = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/

const RefProperty = { ref: z.string().regex(CUSTOM_DATA_REF).optional() }

export const SidebarSectionTextComponent = z.object({
	type: z.literal("text"),
	color: z.enum(["normal", "muted"]),
	size: z.enum(["s", "m", "l"]),
	text: z.string(),
	...RefProperty,
})
export type SidebarSectionTextComponent = z.infer<
	typeof SidebarSectionTextComponent
>

export const SidebarSectionBadgeComponent = z.object({
	type: z.literal("badge"),
	color: z.enum(["green", "gray", "red"]),
	text: z.string(),
	...RefProperty,
})
export type SidebarSectionBadgeComponent = z.infer<
	typeof SidebarSectionBadgeComponent
>

export const SidebarSectionSpacerComponent = z.object({
	type: z.literal("spacer"),
	size: z.enum(["xs", "s", "m", "l", "xl"]),
})
export type SidebarSectionSpacerComponent = z.infer<
	typeof SidebarSectionSpacerComponent
>

export const SidebarSectionDividerComponent = z.object({
	type: z.literal("divider"),
	spacing: z.enum(["xs", "s", "m", "l", "xl"]),
})
export type SidebarSectionDividerComponent = z.infer<
	typeof SidebarSectionDividerComponent
>

// Renders into an <a href>, so anything but http(s) is an injection vector.
export function isSafeHttpUrl(value: string): boolean {
	try {
		const { protocol } = new URL(value)
		return protocol === "http:" || protocol === "https:"
	} catch {
		return false
	}
}

export const SidebarSectionLinkButtonComponent = z.object({
	type: z.literal("linkButton"),
	label: z.string(),
	url: z.string().refine(isSafeHttpUrl, {
		message: "must be an http(s) URL",
	}),
})
export type SidebarSectionLinkButtonComponent = z.infer<
	typeof SidebarSectionLinkButtonComponent
>

export const SidebarSectionCopyButtonComponent = z.object({
	type: z.literal("copyButton"),
	tooltip: z.string().optional(),
	value: z.string(),
	...RefProperty,
})
export type SidebarSectionCopyButtonComponent = z.infer<
	typeof SidebarSectionCopyButtonComponent
>

export const SidebarSectionCopyFieldComponent = z.object({
	type: z.literal("copyField"),
	value: z.string(),
	tooltip: z.string().optional(),
	...RefProperty,
})
export type SidebarSectionCopyFieldComponent = z.infer<
	typeof SidebarSectionCopyFieldComponent
>

export const CUSTOM_DATA_ACTION_NAME = /^[a-zA-Z0-9_-]{1,64}$/

export const SidebarSectionActionButtonComponent = z.object({
	type: z.literal("actionButton"),
	label: z.string().min(1).max(60),
	action: z.string().regex(CUSTOM_DATA_ACTION_NAME),
	input: z.record(z.unknown()).optional(),
	variant: z.enum(["default", "outline", "negative"]).optional(),
	confirm: z
		.object({
			title: z.string().max(80),
			description: z.string().max(280).optional(),
			confirmText: z.string().max(30).optional(),
		})
		.optional(),
})
export type SidebarSectionActionButtonComponent = z.infer<
	typeof SidebarSectionActionButtonComponent
>

const SidebarSectionComponentsForRow = z.discriminatedUnion("type", [
	SidebarSectionTextComponent,
	SidebarSectionBadgeComponent,
	SidebarSectionSpacerComponent,
	SidebarSectionLinkButtonComponent,
	SidebarSectionCopyButtonComponent,
	SidebarSectionCopyFieldComponent,
	SidebarSectionActionButtonComponent,
])

export const SidebarSectionRowComponent = z.object({
	type: z.literal("row"),
	left: z.array(SidebarSectionComponentsForRow),
	right: z.array(SidebarSectionComponentsForRow),
})
export type SidebarSectionRowComponent = z.infer<
	typeof SidebarSectionRowComponent
>

// copy button: tooltip, value

export const SidebarSectionComponent = z.discriminatedUnion("type", [
	SidebarSectionTextComponent,
	SidebarSectionBadgeComponent,
	SidebarSectionRowComponent,
	SidebarSectionSpacerComponent,
	SidebarSectionLinkButtonComponent,
	SidebarSectionDividerComponent,
	SidebarSectionCopyButtonComponent,
	SidebarSectionCopyFieldComponent,
	SidebarSectionActionButtonComponent,
])
export type SidebarSectionComponent = z.infer<typeof SidebarSectionComponent>

export const MAX_COMPONENTS_PER_RESPONSE = 100
export const MAX_ACTIONS_PER_PROVIDER = 25
export const MAX_DATA_REFS_PER_RESPONSE = 50

// A value named for a tool policy without rendering it. Never in the sidebar,
// never in the text the agent reads.
export const SidebarSectionDataRef = z.object({
	ref: z.string().regex(CUSTOM_DATA_REF),
	value: z.union([
		z.string().max(MAX_RESOLVED_REF_LENGTH),
		z.number().finite(),
		z.boolean(),
	]),
})
export type SidebarSectionDataRef = z.infer<typeof SidebarSectionDataRef>

export const SidebarSectionResponse = z.object({
	components: z.array(SidebarSectionComponent).max(MAX_COMPONENTS_PER_RESPONSE),
	data: z
		.array(SidebarSectionDataRef)
		.max(MAX_DATA_REFS_PER_RESPONSE)
		.optional(),
})
export type SidebarSectionResponse = z.infer<typeof SidebarSectionResponse>

// JSON Schema for an action's input. Pinned to an object root because it feeds
// straight into an AI SDK tool definition.
const ActionInputJsonSchema = z
	.object({
		type: z.literal("object"),
		properties: z.record(z.unknown()).optional(),
		required: z.array(z.string()).optional(),
	})
	.passthrough()

export const CustomDataAction = z.object({
	name: z.string().regex(CUSTOM_DATA_ACTION_NAME),
	title: z.string().max(60).optional(),
	description: z.string().min(1).max(1024),
	inputSchema: ActionInputJsonSchema.default({
		type: "object",
		properties: {},
	}),
	requiresConfirmation: z.boolean().optional().default(false),
})
export type CustomDataAction = z.infer<typeof CustomDataAction>

export const CustomDataActionsResponse = z.object({
	actions: z.array(CustomDataAction).max(MAX_ACTIONS_PER_PROVIDER),
})
export type CustomDataActionsResponse = z.infer<
	typeof CustomDataActionsResponse
>

export const CustomDataActionResult = z.object({
	success: z.boolean(),
	message: z.string().max(2000).optional(),
	components: z
		.array(SidebarSectionComponent)
		.max(MAX_COMPONENTS_PER_RESPONSE)
		.optional(),
})
export type CustomDataActionResult = z.infer<typeof CustomDataActionResult>

const AGENT_TEXT_MAX_CHARS = 4000

function describeComponent(component: SidebarSectionComponent): string | null {
	switch (component.type) {
		case "text":
			return component.text
		case "badge":
			return component.text
		case "copyButton":
		case "copyField":
			return component.value
		case "linkButton":
			return `${component.label}: ${component.url}`
		case "actionButton":
			return null
		case "spacer":
		case "divider":
			return null
		case "row": {
			const left = component.left.map(describeComponent).filter(Boolean)
			const right = component.right.map(describeComponent).filter(Boolean)
			if (!left.length && !right.length) return null
			return `${left.join(" ")}: ${right.join(" ")}`.trim()
		}
	}
}

// Projects a component tree to the plain text the agent reads. Buttons are
// dropped because the agent reaches actions through tools, not through UI.
export function flattenComponentsForAgent(
	components: SidebarSectionComponent[],
): string {
	const text = components
		.map(describeComponent)
		.filter((line): line is string => Boolean(line?.trim()))
		.join("\n")
	return text.length > AGENT_TEXT_MAX_CHARS
		? `${text.slice(0, AGENT_TEXT_MAX_CHARS)}\n[truncated]`
		: text
}

function namedValue(
	component: SidebarSectionComponent,
): { ref: string; value: string } | null {
	switch (component.type) {
		case "text":
		case "badge":
			return component.ref
				? { ref: component.ref, value: component.text }
				: null
		case "copyButton":
		case "copyField":
			return component.ref
				? { ref: component.ref, value: component.value }
				: null
		default:
			return null
	}
}

/**
 * Collects every named value a connector advertised, keyed by ref. Two components
 * sharing a ref are both kept so the caller can refuse rather than pick one.
 */
export function collectComponentRefs(
	components: SidebarSectionComponent[],
): Map<string, string[]> {
	const collected = new Map<string, string[]>()

	const visit = (component: SidebarSectionComponent): void => {
		if (component.type === "row") {
			component.left.forEach(visit)
			component.right.forEach(visit)
			return
		}
		const named = namedValue(component)
		if (!named) return
		const existing = collected.get(named.ref)
		if (existing) existing.push(named.value)
		else collected.set(named.ref, [named.value])
	}

	components.forEach(visit)
	return collected
}

/**
 * Every named value a connector advertised, rendered or not. A ref carried by a
 * component and repeated in `data` is kept twice so the caller refuses.
 */
export function collectResponseRefs(
	response: SidebarSectionResponse,
): Map<string, string[]> {
	const collected = collectComponentRefs(response.components)

	for (const entry of response.data ?? []) {
		const existing = collected.get(entry.ref)
		if (existing) existing.push(String(entry.value))
		else collected.set(entry.ref, [String(entry.value)])
	}

	return collected
}

function formatIssuePath(path: Array<string | number>): string {
	const formatted = path.reduce<string>(
		(acc, segment) =>
			typeof segment === "number"
				? `${acc}[${segment}]`
				: acc
					? `${acc}.${segment}`
					: String(segment),
		"",
	)
	return formatted || "response"
}

function formatIssue(issue: z.ZodIssue): string {
	const at = formatIssuePath(issue.path)
	switch (issue.code) {
		case "invalid_enum_value":
			return `${at}: received "${String(issue.received)}", allowed values: ${issue.options.join(", ")}`
		case "invalid_union_discriminator":
			return `${at}: unknown component type, allowed values: ${issue.options.join(", ")}`
		case "invalid_type":
			return `${at}: expected ${issue.expected}, received ${issue.received}`
		default:
			return `${at}: ${issue.message}`
	}
}

const MAX_REPORTED_ISSUES = 3

export function formatSidebarSectionError(error: z.ZodError): string {
	const issues = error.issues.slice(0, MAX_REPORTED_ISSUES).map(formatIssue)
	const more =
		error.issues.length > MAX_REPORTED_ISSUES
			? ` (+${error.issues.length - MAX_REPORTED_ISSUES} more)`
			: ""
	return `Invalid response: ${issues.join("; ")}${more}`
}
